import { memo, ReactComponentElement, ReactNode, useState } from 'react';
import cls from 'classnames';

import Button, { ButtonProps } from '../Button/Button';
import FancyPopup, { FancyPopupOption } from '../FancyPopup/FancyPopup';

import styles from './FancyDropdownButton.module.css';

export interface FancyDropdownButtonProps
  extends Omit<ButtonProps, 'onClick'>, Pick<FancyPopupOption, 'strategy' | 'placement' | 'usePortal'> {
  children: ReactComponentElement<Any>;
  label: ReactNode;
  disabled?: boolean;
  withTriangle?: boolean;
  popupClassName?: string;
  onClickOutside?: (e: MouseEvent) => void;

  // for controlled component
  isOpen?: boolean;
  onChangeState?: (state: boolean) => void;
}

/** @example
 * const [isOpened, setIsOpen] = useState(false);
 *
 * <FancyDropdownButton
 *   isOpen={isOpened}
 *   onChangeState={setIsOpen}
 *   label="New"
 *   size="m"
 *   {...otherButtonProps}
 * >
 *   <MenuList>
 *     <MenuItem onClick={() => {}}>
 *       Option 1
 *     </MenuItem>
 *
 *     <MenuItem onClick={() => {}}>
 *       Option 2
 *     </MenuItem>
 *   </MenuList>
 * </FancyDropdownButton>
 */
function FancyDropdownButton(props: FancyDropdownButtonProps) {
  const {
    disabled,
    children,
    label,
    withTriangle,
    isOpen,
    onChangeState,
    placement,
    strategy,
    usePortal,
    popupClassName,
    onClickOutside,
    ...rest
  } = props;

  const [isOpened, setIsOpen] = useState(false);

  const isOpenState = typeof isOpen === 'boolean' ? isOpen : isOpened;

  function onChangeStateHandler(state: boolean): void {
    if (typeof onChangeState === 'function') {
      onChangeState(state);
    } else {
      setIsOpen(state);
    }
  }

  function onClickOutsideHandler(e: MouseEvent): void {
    if (typeof onClickOutside === 'function') {
      onClickOutside(e);
    } else {
      onChangeStateHandler(false);
    }
  }

  const btn = (
    <Button disabled={disabled} {...rest}>
      {label}

      {withTriangle && (
        <span className={cls(styles.dropdownTriangle, { [styles.dropdownTriangleOpened]: isOpenState })} />
      )}
    </Button>
  );

  if (disabled) {
    return btn;
  }

  return (
    <FancyPopup
      action="click"
      trigger={btn}
      body={children}
      placement={placement}
      strategy={strategy}
      usePortal={usePortal}
      isOpen={isOpenState}
      onChangeState={onChangeStateHandler}
      onClickOutside={onClickOutsideHandler}
      className={cls(styles.content, popupClassName)}
    />
  );
}

export default memo(FancyDropdownButton);
